import { useField } from 'formik';
import { useQuery } from 'react-query';
import { Autocomplete } from 'src/components/Autocomplete';
import { TextField } from 'src/components/TextField';
import { fetchAssessmentOptions } from 'src/models/assessment/assessment';

interface AssessmentSelectProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  multiple?: boolean;
}

interface FormAssessmentSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
}

export const AssessmentSelect: React.FC<AssessmentSelectProps> = ({
  value,
  onChange,
  label = 'Assessment',
  placeholder = 'Select Assessment',
  disabled = false,
  error,
  helperText,
  multiple = false,
}) => {
  const { data: assessments, isLoading } = useQuery({
    queryKey: ['assessments', { fetchAll: true }],
    queryFn: async () => {
      const response = await fetchAssessmentOptions();
      return response || [];
    },
    staleTime: 60000,
    cacheTime: 60000,
  });

  const options = (assessments || []).map((assessment) => ({
    value: assessment.id.toString(),
    label: assessment.name,
  }));

  if (multiple) {
    return (
      <Autocomplete
        multiple
        options={options}
        value={
          Array.isArray(value)
            ? value.map((v) => ({
                value: v,
                label: options.find((o) => o.value === v)?.label || v,
              }))
            : []
        }
        onChange={(_, newValue) => {
          onChange(newValue.map((option) => option.value));
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            disabled={isLoading || disabled}
          />
        )}
      />
    );
  }

  return (
    <TextField
      label={label}
      error={error}
      helperText={helperText}
      disabled={isLoading || disabled}
      options={[{ value: '', label: placeholder, disabled: true }, ...options]}
      value={value as string}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export const FormAssessmentSelect: React.FC<FormAssessmentSelectProps> = ({
  name,
  label,
  placeholder,
  disabled,
  multiple,
}) => {
  const [field, meta, helpers] = useField({ name });

  return (
    <AssessmentSelect
      value={field.value || (multiple ? [] : '')}
      onChange={(value) => helpers.setValue(value)}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      error={!!(meta.touched && meta.error)}
      helperText={meta.touched ? meta.error : undefined}
      multiple={multiple}
    />
  );
};
